import { type ReactNode } from 'react';
import { SearchInput, Tooltip } from '@qodo/design-system';
import { ChevronRight, Folder, GitBranch, Home } from 'lucide-react';
import { InstallationStatusMessage } from 'shared/InstallationDialog';
import { Checkbox } from 'ui/checkbox';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from 'ui/pagination';
import { cn } from 'utils/className';
import { Loader } from 'components/Loader';
import { type ScopeBrowserState } from './useScopeBrowser';
import type { ScopeBrowserLabels, ScopeRowState } from './types';
import type {
  GitlabProvisioningSelectGroupItem,
  GitlabProvisioningSelectProjectItem,
} from 'network/httpClients/GitlabClient';

type SelectCheckboxProps = {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onToggle: () => void;
  /** When set, a disabled checkbox explains why via an on-hover tooltip. */
  tooltip?: string;
  /** Dim the box so a locked row reads as non-actionable, matching its label/icon. */
  dimmed?: boolean;
};

/**
 * A scope-row checkbox. When `tooltip` is set (a non-selectable scope — already
 * connected, or covered by its group), the checkbox is wrapped in a tooltip that
 * explains why it can't be toggled.
 */
const SelectCheckbox = ({
  checked,
  disabled,
  ariaLabel,
  onToggle,
  tooltip,
  dimmed,
}: SelectCheckboxProps) => {
  const checkbox = (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={onToggle}
      aria-label={ariaLabel}
      // The DS disabled fill matches this dark panel, so a disabled (unchecked) box
      // vanishes. Keep the idle border/fill so it stays visible; when `dimmed`, drop
      // it to opacity-50 so the whole locked row (box + icon + label) grays uniformly.
      className={cn(
        'disabled:border-border-input-neutral-idle disabled:bg-fill-input-neutral-idle',
        dimmed && 'opacity-50',
      )}
    />
  );

  if (!tooltip) return checkbox;

  return (
    <Tooltip content={tooltip} triggerProps={{ asChild: true }}>
      <span className="flex">{checkbox}</span>
    </Tooltip>
  );
};

type ScopeRowLabelProps = {
  icon: ReactNode;
  label: string;
  /** Gray the icon + name together when the row is locked / non-actionable. */
  dimmed?: boolean;
  /** When set, hovering the icon or name explains why the row is locked. */
  tooltip?: string;
};

/**
 * A scope row's icon + name. When `tooltip` is set (a locked row — already
 * connected, covered by its group, or owner-gated), hovering the icon or the name
 * surfaces the same explanation as the checkbox, so the whole row is discoverable.
 */
const ScopeRowLabel = ({
  icon,
  label,
  dimmed,
  tooltip,
}: ScopeRowLabelProps) => {
  const content = (
    <span className={cn('flex items-center gap-2', dimmed && 'opacity-50')}>
      {icon}
      <span className="text-sm text-text-general-neutral-strong">{label}</span>
    </span>
  );

  if (!tooltip) return content;

  return (
    <Tooltip content={tooltip} triggerProps={{ asChild: true }}>
      {content}
    </Tooltip>
  );
};

type ScopeBrowserViewProps = {
  browser: ScopeBrowserState;
  labels: ScopeBrowserLabels;
  /** Per-row checkbox state for a group, from the container's coverage/selection policy. */
  getGroupRow: (group: GitlabProvisioningSelectGroupItem) => ScopeRowState;
  /** Per-row checkbox state for a project, from the container's coverage/selection policy. */
  getProjectRow: (
    project: GitlabProvisioningSelectProjectItem,
  ) => ScopeRowState;
  onToggleGroup: (group: GitlabProvisioningSelectGroupItem) => void;
  onToggleProject: (project: GitlabProvisioningSelectProjectItem) => void;
  /**
   * Suppress the in-list error banner (the container surfaces the error itself —
   * e.g. Manage shows a PAT-token error at the PAT field). The list box renders
   * empty instead of the generic error+retry banner.
   */
  suppressError?: boolean;
  /** Header slot rendered above the search box (adopt banner, selection counts, …). */
  children?: ReactNode;
};

/**
 * Mode-agnostic presentation of the v2 GitLab scope tree: header slot, search,
 * breadcrumb, the merged group/project list (loading / error / empty / rows), and
 * pagination. Selection + coverage policy come from the container via `getGroupRow`
 * / `getProjectRow`; this view only renders. Shared by the install Select step and
 * the Manage modal.
 */
export const ScopeBrowserView = ({
  browser,
  labels,
  getGroupRow,
  getProjectRow,
  onToggleGroup,
  onToggleProject,
  suppressError = false,
  children,
}: ScopeBrowserViewProps) => {
  const {
    searchInput,
    setSearchInput,
    isSearching,
    currentPage,
    setCurrentPage,
    breadcrumbs,
    onDrillDown,
    onNavigateToLevel,
    items,
    isLoading,
    isError,
    hasItems,
    hasMore,
    refetch,
    isFetching,
  } = browser;

  return (
    <div className="flex flex-col gap-3">
      {children}

      <SearchInput
        placeholder={labels.searchPlaceholder}
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
      />

      {/* Breadcrumb navigation — always visible */}
      <div className="flex items-center gap-1 text-sm text-text-general-neutral-subtle">
        <button
          type="button"
          onClick={() => onNavigateToLevel(-1)}
          className="flex items-center gap-1 hover:text-text-general-neutral-strong"
          disabled={isSearching}
          aria-label={labels.breadcrumbRoot}
        >
          <Home className="size-4" />
        </button>
        {!isSearching &&
          breadcrumbs.map((crumb, index) => (
            <div key={crumb.id} className="flex items-center gap-1">
              <ChevronRight className="size-4" />
              <button
                type="button"
                onClick={() => onNavigateToLevel(index)}
                className="hover:text-text-general-neutral-strong"
              >
                {crumb.name}
              </button>
            </div>
          ))}
      </div>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-border-bg-neutral">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : isError && !suppressError ? (
          <div className="p-3">
            <InstallationStatusMessage
              status="error"
              message={labels.errorMessage}
              retryLabel={labels.retryLabel}
              onRetry={() => refetch()}
              isRetrying={isFetching}
            />
          </div>
        ) : isError ? (
          <div className="py-8" />
        ) : !hasItems ? (
          <div
            role="status"
            className="flex justify-center py-8 text-sm text-text-general-neutral-subtle"
          >
            {labels.emptyMessage}
          </div>
        ) : (
          items.map((item) => {
            if (item.type === 'group') {
              const row = getGroupRow(item);
              // The checkbox grays on its own disabled state; the row (label + icon)
              // grays only when `dimmed` (defaulting to disabled). A tier-gated group
              // sets `dimmed: false` so its checkbox reads disabled while the label
              // and drill-in stay actionable.
              const isRowDimmed = row.dimmed ?? row.disabled;

              return (
                <div
                  key={`group-${item.id}`}
                  className="flex items-center gap-2 py-1.5 px-3 hover:bg-bg-general-neutral-subtle"
                >
                  {row.showCheckbox && (
                    <SelectCheckbox
                      checked={row.checked}
                      disabled={row.disabled}
                      dimmed={row.disabled}
                      tooltip={row.tooltip}
                      ariaLabel={isSearching ? item.fullPath : item.name}
                      onToggle={() => onToggleGroup(item)}
                    />
                  )}
                  <ScopeRowLabel
                    icon={
                      <Folder className="size-4 shrink-0 text-icon-general-neutral-mid" />
                    }
                    label={isSearching ? item.fullPath : item.name}
                    dimmed={isRowDimmed}
                    tooltip={row.tooltip}
                  />
                  {row.badge && (
                    <span className="text-xs text-text-tag-positive-active">
                      {row.badge}
                    </span>
                  )}
                  {/* Drill-in stays available while searching so a result group can be
                      opened; onDrillDown exits search and navigates into it. */}
                  {row.disableDrillIn ? (
                    <Tooltip
                      content={row.tooltip}
                      triggerProps={{ asChild: true }}
                    >
                      <span className="ml-auto flex items-center p-1 text-icon-general-neutral-mid opacity-50">
                        <ChevronRight className="size-4" />
                      </span>
                    </Tooltip>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onDrillDown(item)}
                      className="ml-auto flex items-center rounded p-1 text-icon-general-neutral-mid hover:bg-bg-general-neutral-mid hover:text-icon-general-neutral-strong"
                      aria-label={labels.getDrillInLabel(item.name)}
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  )}
                </div>
              );
            }

            const row = getProjectRow(item);
            const isRowDimmed = row.dimmed ?? row.disabled;

            return (
              <div
                key={`project-${item.id}`}
                className="flex items-center gap-2 py-1.5 px-3 hover:bg-bg-general-neutral-subtle"
              >
                {row.showCheckbox && (
                  <SelectCheckbox
                    checked={row.checked}
                    disabled={row.disabled}
                    dimmed={row.disabled}
                    tooltip={row.tooltip}
                    ariaLabel={isSearching ? item.pathWithNamespace : item.name}
                    onToggle={() => onToggleProject(item)}
                  />
                )}
                <ScopeRowLabel
                  icon={
                    <GitBranch className="size-4 shrink-0 text-icon-general-neutral-mid" />
                  }
                  label={isSearching ? item.pathWithNamespace : item.name}
                  dimmed={isRowDimmed}
                  tooltip={row.tooltip}
                />
                {row.badge && (
                  <span className="text-xs text-text-tag-positive-active">
                    {row.badge}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {!isLoading && hasItems && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                className={
                  currentPage === 1
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer'
                }
              />
            </PaginationItem>
            <PaginationItem>
              <span className="text-sm text-text-general-neutral-subtle">
                {labels.pageLabel}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage((page) => page + 1)}
                className={
                  hasMore ? 'cursor-pointer' : 'pointer-events-none opacity-50'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};
